import V2MenuLivePicker from '@/components/v2/menu-import/workspace/V2MenuLivePicker.vue';
import { useGtag } from '@/composition/use-gtag';
import { useModal } from '@/composition/use-modal';
import { useUi } from '@/composition/use-ui';
import { keepPriceOp, keepSizesOp } from '@/domain/menu-import/live-rows';
import {
  changedItemCount,
  cleanDraftName,
  hideUnpricedOps,
} from '@/domain/menu-import/workspace-view';
import { AlertType } from '@/interfaces/IAlert';
import type {
  IAnswerQuestionDto,
  TDraftQuestion,
} from '@/interfaces/IMenuImport';
import { i18n } from '@/plugins/i18n';
import { useMenuImport } from './menu-import-context';

/** Temp ids of the records these actions add (never shaped like a draft id). */
const NEW_ITEM_TEMP_ID = 'newItem';
const NEW_CATEGORY_TEMP_ID = 'newCategory';

export interface IUseDraftActionsOptions {
  /** The store's country (price formats of the dish picker). */
  country: () => string;
  /** Shows the chat: a combo the owner wants split goes to the assistant (§4.5.3). */
  openAssistant?: () => void;
}

/** A toast action that undoes the change the toast announced. */
export interface IUndoToastAction {
  text: string;
  cb: () => void;
}

/**
 * The owner's direct edits of the menu draft from the workspace list, the
 * question cards and the review queue (plan §4.5): typed operations sent
 * through the session's serial queue, with their confirms, toasts and
 * events. Every write error is already toasted by the session. Call it in
 * a component's setup (it uses the modal service).
 */
export function useDraftActions(options: IUseDraftActionsOptions) {
  const ctx = useMenuImport();
  const modals = useModal();
  const ui = useUi();
  const gtag = useGtag();
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(i18n.t(key, params));
  const trc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(i18n.tc(key, count, params));

  /**
   * «Deshacer» on a toast: undoes the change it announced (the draft is still
   * at `rev`, the rev that change left), never a later one.
   */
  function undoAction(rev: number): IUndoToastAction {
    return { text: tr('menuImport.undo'), cb: () => void ctx.undo(rev) };
  }

  /** «Está bien». */
  async function confirmItem(itemId: string): Promise<boolean> {
    const result = await ctx.mutate([
      { op: 'confirmReview', itemIds: [itemId] },
    ]);
    return result !== null;
  }

  /** The price typed in a dish row (already parsed and > 0). */
  async function setItemPrice(itemId: string, price: number): Promise<boolean> {
    const result = await ctx.mutate([{ op: 'updateItem', itemId, price }]);
    return result !== null;
  }

  /** «Ocultar hasta tener precio». */
  async function hideItem(itemId: string): Promise<boolean> {
    const result = await ctx.mutate([
      { op: 'updateItem', itemId, hidden: true },
    ]);
    return result !== null;
  }

  /** «Ocultar {count} sin precio»: one change, undone with one «Deshacer». */
  async function hideUnpriced(): Promise<boolean> {
    const draft = ctx.draft.value;
    const ops = draft ? hideUnpricedOps(draft) : [];
    if (ops.length === 0) return false;
    const result = await ctx.mutate(ops);
    if (!result) return false;
    ui.showToast(
      AlertType.Success,
      trc('menuImport.review.hiddenToast', ops.length, { count: ops.length }),
      undoAction(result.delta.rev)
    );
    return true;
  }

  /** Saves a category name typed inline; nothing is sent when empty or unchanged. */
  async function renameCategory(
    categoryId: string,
    text: string
  ): Promise<boolean> {
    const category = ctx.draft.value?.categories[categoryId];
    const name = cleanDraftName(text);
    if (!category || name === null || name === category.name) return false;
    const result = await ctx.mutate([
      { op: 'updateCategory', categoryId, name },
    ]);
    return result !== null;
  }

  /** «Quitar categoría»: asks first when it has dishes (they go with it). */
  async function removeCategory(categoryId: string): Promise<boolean> {
    const category = ctx.draft.value?.categories[categoryId];
    if (!category) return false;
    const count = category.itemIds.length;
    if (count > 0) {
      // Dishes already on the menu stay on it: removing them here only drops their update.
      const linked = category.itemIds.filter(
        (id) => !!ctx.draft.value?.items[id]?.live?.dish
      ).length;
      const removed = trc('menuImport.editor.removeCategoryMessage', count, {
        count,
      });
      const kept =
        linked > 0
          ? trc('menuImport.update.removeCategoryLinked', linked, {
              count: linked,
            })
          : null;
      const confirmed = await modals.confirm({
        title: tr('menuImport.editor.removeCategoryTitle'),
        message: kept === null ? removed : `${removed} ${kept}`,
        acceptText: tr('menuImport.editor.removeCategory'),
        cancelText: tr('cancel'),
        acceptColor: 'error',
      });
      if (!confirmed) return false;
    }
    const result = await ctx.mutate([{ op: 'deleteCategory', categoryId }]);
    return result !== null;
  }

  /** «Agregar plato»: «Plato nuevo» at the end of the category, its editor opened. */
  async function addItem(categoryId: string): Promise<string | null> {
    const result = await ctx.mutate([
      {
        op: 'addItem',
        tempId: NEW_ITEM_TEMP_ID,
        categoryId,
        name: tr('menuImport.editor.newItemName'),
      },
    ]);
    const itemId = result?.tempIds[NEW_ITEM_TEMP_ID] ?? null;
    if (itemId !== null) ctx.openItemId.value = itemId;
    return itemId;
  }

  /** «Agregar categoría»: «Categoría nueva» last; the caller opens its name. */
  async function addCategory(): Promise<string | null> {
    const order = ctx.draft.value?.categoryOrder ?? [];
    const result = await ctx.mutate([
      {
        op: 'addCategory',
        tempId: NEW_CATEGORY_TEMP_ID,
        name: tr('menuImport.editor.newCategoryName'),
        afterCategoryId: order.length > 0 ? order[order.length - 1] : null,
      },
    ]);
    return result?.tempIds[NEW_CATEGORY_TEMP_ID] ?? null;
  }

  /**
   * An answer to a question card: «Listo. Cambiamos {count} platos.» with
   * «Deshacer»; a combo to split is then asked to the assistant.
   */
  async function answerQuestion(
    question: TDraftQuestion,
    body: IAnswerQuestionDto
  ): Promise<boolean> {
    const delta = await ctx.answer(question.id, body);
    if (!delta) return false;
    gtag.event('menu_question_answered', {
      kind: question.kind,
      answer: body.answer,
    });
    const count = changedItemCount(delta);
    if (count > 0)
      ui.showToast(
        AlertType.Success,
        trc('menuImport.questions.answered', count, { count }),
        undoAction(delta.rev)
      );
    // The dishes marked to hide once the answer is in (a replayed or
    // change-less answer carries no `liveHiddenIds`).
    const hidden =
      question.kind === 'new_menu' && body.answer === 'hide'
        ? ctx.draft.value?.live?.hiddenIds.length ?? 0
        : 0;
    if (hidden > 0) {
      ui.showToast(
        AlertType.Success,
        trc('menuImport.questions.newMenu.hidden', hidden, { count: hidden }),
        undoAction(delta.rev)
      );
    }
    if (question.kind === 'combo' && body.answer === 'split') {
      void ctx.chat.send(
        tr('menuImport.questions.combo.splitMessage', {
          name: question.params.name,
        }),
        question.itemIds
      );
      options.openAssistant?.();
    }
    return true;
  }

  // Update sessions (a store with dishes, plan §3.5)

  /**
   * Links a dish to one of the store's menu («Sí, es el mismo», a picked
   * dish), or makes it a new dish when `liveItemId` is null («No, es otro»,
   * «Es un plato nuevo»).
   */
  function linkLive(
    itemId: string,
    liveItemId: number | null
  ): Promise<boolean> {
    return ctx.refreshLive({ link: { itemId, liveItemId } });
  }

  /** «Dejar {price}», «Dejar el precio de hoy»: today's price stays. */
  async function keepLivePrice(itemId: string): Promise<boolean> {
    const item = ctx.draft.value?.items[itemId];
    if (!item) return false;
    return (await ctx.mutate([keepPriceOp(item)])) !== null;
  }

  /** «Dejar los de hoy»: today's size prices stay. */
  async function keepLiveSizes(itemId: string): Promise<boolean> {
    const item = ctx.draft.value?.items[itemId];
    if (!item) return false;
    return (await ctx.mutate([keepSizesOp(item)])) !== null;
  }

  /** «Mostrarlo»: a dish hidden on the menu shows again when publishing. */
  async function showItem(itemId: string): Promise<boolean> {
    const result = await ctx.mutate([
      { op: 'updateItem', itemId, hidden: false },
    ]);
    return result !== null;
  }

  /** «Ocultar», «No ocultar», «Ocultar los {count}»: dishes left out of the photos. */
  function hideOthers(
    liveItemIds: number[],
    hidden: boolean
  ): Promise<boolean> {
    if (liveItemIds.length === 0) return Promise.resolve(false);
    return ctx.refreshLive({ hide: { liveItemIds, hidden } });
  }

  /** «Es otro de mi menú…», «Ya está en mi menú»: the owner picks the dish, then it is linked. */
  async function pickLive(itemId: string): Promise<boolean> {
    const live = ctx.draft.value?.live;
    if (!live || live.others.length === 0) return false;
    const liveItemId = await modals.showDynamicDialog<number | null>(
      V2MenuLivePicker,
      {
        others: live.others,
        categories: live.categories,
        currency: ctx.displayCurrency.value,
        country: options.country(),
      },
      'v2-menu-live-picker',
      { width: 480 }
    );
    if (typeof liveItemId !== 'number') return false;
    return linkLive(itemId, liveItemId);
  }

  return {
    undoAction,
    confirmItem,
    setItemPrice,
    hideItem,
    hideUnpriced,
    renameCategory,
    removeCategory,
    addItem,
    addCategory,
    answerQuestion,
    linkLive,
    keepLivePrice,
    keepLiveSizes,
    showItem,
    hideOthers,
    pickLive,
  };
}

export type TUseDraftActions = ReturnType<typeof useDraftActions>;
